/* eslint-disable react/jsx-no-literals */
// components/BudgetPanel/TotalBudgetView.tsx
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import {
  BudgetSummary,
  FilterBudget,
  SectionBudget,
  TimePeriodBudget,
} from '@/features/canvas/types/budget-type';
import { formatCurrency } from '@/features/canvas/utils/currency';

import { BreakdownSelector } from './BreakdownSelector';

interface Props {
  breakdownView: string;
  currency: string;
  filterBreakdowns: Record<number, FilterBudget[]>;
  isLoading: boolean;
  onBreakdownChange: (value: string) => void;
  sectionBreakdown: SectionBudget[];
  summary: BudgetSummary | undefined;
  timePeriodBreakdown: TimePeriodBudget[];
}

export const TotalBudgetView: React.FC<Props> = ({
  summary,
  timePeriodBreakdown,
  sectionBreakdown,
  filterBreakdowns,
  currency,
  isLoading,
  breakdownView,
  onBreakdownChange,
}) => {
  const { t } = useTranslation();
  const { filters } = useEditorContext();

  // Calculate total for percentage calculations
  const totalBudget = summary?.total_budget || 0;

  if (isLoading) {
    return <LoadingState>{t('loading')}</LoadingState>;
  }

  // Helper function to render filter breakdowns
  const renderFilterBreakdown = (filterId: number) => {
    const breakdowns = filterBreakdowns[filterId] || [];
    const filter = filters?.find(f => f.id === filterId);

    return breakdowns.map(filterOption => {
      const percentage =
        totalBudget > 0 ? Math.round((filterOption.total_budget / totalBudget) * 100) : 0;

      // Find the color for this option
      const optionColor =
        filter?.options?.find(opt => opt.id === filterOption.filter_option_id)?.color || '#6e6e72';

      return (
        <BreakdownItem key={filterOption.filter_option_id}>
          <ItemGrid>
            <ItemHeader>
              <ItemTitleWrapper>
                <ColorCircle $color={optionColor} />
                <ItemTitle>{filterOption.filter_option_title}</ItemTitle>
              </ItemTitleWrapper>
              <ItemPercentage>{percentage}%</ItemPercentage>
            </ItemHeader>
            <Divider />
            <ItemValue>{formatCurrency(filterOption.total_budget, currency)}</ItemValue>
          </ItemGrid>
        </BreakdownItem>
      );
    });
  };

  return (
    <Container>
      {/* Breakdown Selector and View */}
      <Section>
        <BreakdownHeader>
          <DisplayByLabel>Display by:</DisplayByLabel>
          <BreakdownSelector value={breakdownView} onChange={onBreakdownChange} />
        </BreakdownHeader>

        <BreakdownList>
          {breakdownView === 'timePeriod' &&
            timePeriodBreakdown?.map(period => {
              const percentage =
                totalBudget > 0 ? Math.round((period.total_budget / totalBudget) * 100) : 0;

              return (
                <BreakdownItem key={period.time_period_id}>
                  <ItemGrid>
                    <ItemHeader>
                      <ItemTitle>{period.time_period}</ItemTitle>
                      <ItemPercentage>{percentage}%</ItemPercentage>
                    </ItemHeader>
                    <Divider />
                    <ItemValue>{formatCurrency(period.total_budget, currency)}</ItemValue>
                  </ItemGrid>
                </BreakdownItem>
              );
            })}

          {breakdownView === 'section' &&
            sectionBreakdown?.map(section => {
              const percentage =
                totalBudget > 0 ? Math.round((section.total_budget / totalBudget) * 100) : 0;

              return (
                <BreakdownItem key={section.section_id}>
                  <ItemGrid>
                    <ItemHeader>
                      <ItemTitle>{section.section}</ItemTitle>
                      <ItemPercentage>{percentage}%</ItemPercentage>
                    </ItemHeader>
                    <Divider />
                    <ItemValue>{formatCurrency(section.total_budget, currency)}</ItemValue>
                  </ItemGrid>
                </BreakdownItem>
              );
            })}

          {breakdownView.startsWith('filter-') &&
            renderFilterBreakdown(parseInt(breakdownView.replace('filter-', '')))}
        </BreakdownList>
      </Section>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 15px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BreakdownHeader = styled.div`
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const DisplayByLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #363649;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
`;

const BreakdownList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BreakdownItem = styled.div`
  padding: 1rem;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0.5rem;
`;

const ItemGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ColorCircle = styled.div<{ $color: string }>`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  background-color: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const ItemTitle = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: #9a9aa3;
`;

const ItemPercentage = styled.span`
  font-size: 1.5rem;
  font-weight: 600;
  color: #363649;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #26a5a2;
  opacity: 32%;
  margin-bottom: 1.25rem;
`;

const ItemValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #616161;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${({ theme }) => theme.palette.text.secondary};
`;
